import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconVoid, IconUndo } from '../icons';
import { StatusChip } from './common/StatusChip';

/**
 * `CancellationStatusChip` (plan-ux-status-chips-storno-inbox.md §A).
 *
 * Thin domain wrapper around {@link StatusChip} for the invoice Storno
 * lifecycle — 1:1 pattern with `EmailStatusChip`: the visible chip shows
 * ONLY the symbol icon (Daniel-Entscheidung D2 „Nur Symbol"), the status
 * word (`invoice:cancellation.status.*`) is exposed as a screenreader-only
 * `srLabel` and additionally shown as a visible bold first line inside the
 * popover, with the longer detail sentence underneath.
 *
 * Semantics (unchanged): `isCancellation` = this invoice IS a cancellation
 * invoice (type 381, corrective document). `isCancelled` = this invoice WAS
 * voided by a cancellation invoice. Both flags are independent — if both are
 * set, TWO chips render (DOM order unchanged: cancelled first, then
 * isCancellation).
 *
 * i18n-Namespace: `invoice` (the `cancellation.*` keys live in
 * `invoice.json`, not `common.json`). `useTranslation(['invoice'])` makes
 * `invoice` the DEFAULT namespace, so keys are used unprefixed
 * (`cancellation.status.cancelled`, not `invoice:cancellation.status.cancelled`)
 * — this matches the pre-existing keys the replaced inline spans used
 * (`InvoiceArchive.dataTableMigration.test.tsx` key-echo-mock asserts the
 * exact unprefixed strings).
 */
export interface CancellationStatusChipProps {
  isCancelled?: boolean;
  isCancellation?: boolean;
  cancellationInvoiceNumber?: string | null;
}

export function CancellationStatusChip({
  isCancelled,
  isCancellation,
  cancellationInvoiceNumber,
}: CancellationStatusChipProps) {
  const { t } = useTranslation(['invoice']);

  // Kein Flag gesetzt ⇒ null (Parität zum ersetzten Bestand).
  if (!isCancelled && !isCancellation) return null;

  const detailTitle = t('cancellation.statusDetail.title');

  const cancelledShortLabel = t('cancellation.status.cancelled', 'Storniert');
  const cancelledDetail = (
    <div className="flex flex-col gap-0.5">
      <span className="font-semibold text-primary">{cancelledShortLabel}</span>
      <span>{t('cancellation.statusDetail.cancelled')}</span>
    </div>
  );

  const isCancellationShortLabel = t(
    'cancellation.status.isCancellation',
    'Stornorechnung',
  );
  const isCancellationSentence = cancellationInvoiceNumber
    ? t('cancellation.statusDetail.isCancellationFor', {
        invoiceNumber: cancellationInvoiceNumber,
      })
    : t('cancellation.statusDetail.isCancellation');
  const isCancellationDetail = (
    <div className="flex flex-col gap-0.5">
      <span className="font-semibold text-primary">{isCancellationShortLabel}</span>
      <span>{isCancellationSentence}</span>
    </div>
  );

  return (
    <>
      {isCancelled && (
        <StatusChip
          tone="danger"
          icon={
            <span data-testid="cancellation-icon-cancelled">
              <IconVoid size={14} />
            </span>
          }
          label={null}
          srLabel={cancelledShortLabel}
          detailTitle={detailTitle}
          detail={cancelledDetail}
          data-testid="cancellation-status-chip-cancelled"
        />
      )}
      {isCancellation && (
        <StatusChip
          tone="warning"
          icon={
            <span data-testid="cancellation-icon-isCancellation">
              <IconUndo size={14} />
            </span>
          }
          label={null}
          srLabel={isCancellationShortLabel}
          detailTitle={detailTitle}
          detail={isCancellationDetail}
          data-testid="cancellation-status-chip-isCancellation"
        />
      )}
    </>
  );
}

export default CancellationStatusChip;
